import { useEffect, useState } from 'react';
import { TabBar } from '@/components/ui/TabBar';
import { useAuth } from '@/hooks/useAuth';
import TrackingBoard from './TrackingBoard';
import NewArrivalDialog from './NewArrivalDialog';
import TriagePanel from './TriagePanel';
import EncounterWorkspace from './EncounterWorkspace';
import EncounterHistory from './EncounterHistory';
import KpiReportTab from './KpiReportTab';
import { useDeclineNotifier } from './useDeclineNotifier';
import type { ArrivalResult } from './types';

/**
 * The Emergency / Casualty module (docs/HMS_V4_EMERGENCY_CASUALTY_DESIGN.md).
 *
 * Not a stage pipeline (D1): the board is the home screen, an encounter opens
 * as a workspace in place, and every activity — triage, notes, orders, MLC,
 * identity — is available at any time between arrival and departure. Only
 * the three milestones (triaged, disposition decided, departed) are sequential.
 *
 * Tabs here are READ-able by anyone holding `emergency`; what a user can DO
 * is gated per action by the er:* grants, re-checked server-side.
 */
type Tab = 'board' | 'history' | 'kpi';

export default function EmergencyModule({ openEncounterId, onOpenConsumed }: {
  /** Set by App.tsx when the doctor accepts an ED page from the popup. */
  openEncounterId?: number | null;
  onOpenConsumed?: () => void;
} = {}) {
  const { user } = useAuth();
  const [tab, setTab] = useState<Tab>('board');
  const [openId, setOpenId] = useState<number | null>(openEncounterId ?? null);
  useEffect(() => {
    if (!openEncounterId) return;
    setOpenId(openEncounterId);
    onOpenConsumed?.();
  }, [openEncounterId, onOpenConsumed]);
  const [arrivalOpen, setArrivalOpen] = useState(false);
  const [triageAfterArrival, setTriageAfterArrival] = useState<ArrivalResult | null>(null);

  const modules = user?.modules ?? [];
  // A paged doctor said "can't come" (or did not answer) → tell ED staff.
  useDeclineNotifier(setOpenId, modules.includes('emergency') && modules.includes('er:clinical'));
  if (!modules.includes('emergency')) {
    return <div className="rounded-xl border border-border bg-card p-6 text-[13px] text-ink-muted">You don’t have access to the Emergency module.</div>;
  }
  const canRegister = modules.includes('er:register');
  const tabs: Array<{ id: Tab; label: string }> = [
    { id: 'board', label: '🚑 Tracking board' },
    { id: 'history', label: '📋 Encounters' },
    ...(modules.includes('reports') ? [{ id: 'kpi' as Tab, label: '📊 ED KPIs' }] : []),
  ];

  if (openId) {
    return <EncounterWorkspace encounterId={openId} onBack={() => setOpenId(null)} />;
  }

  return (
    <div className="touch-form fade-in">
      <TabBar tabs={tabs} active={tab} onChange={setTab} className="mb-4" />
      {tab === 'board' && <TrackingBoard onOpen={setOpenId} onNewArrival={() => setArrivalOpen(true)} canRegister={canRegister} />}
      {tab === 'history' && <EncounterHistory onOpen={setOpenId} />}
      {tab === 'kpi' && <KpiReportTab />}

      <NewArrivalDialog
        open={arrivalOpen}
        onClose={() => setArrivalOpen(false)}
        onCreated={(enc) => {
          setArrivalOpen(false);
          // "Save → lands on Triage" (§7) — for whoever may triage. Anyone
          // else (front desk) goes back to the board, where the new row sits
          // at the top as "awaiting triage".
          if (modules.includes('er:triage')) setTriageAfterArrival(enc);
        }}
      />
      {triageAfterArrival && (
        <TriagePanel
          encounter={triageAfterArrival}
          open
          onClose={() => setTriageAfterArrival(null)}
          onDone={() => setOpenId(triageAfterArrival.id)}
        />
      )}
    </div>
  );
}
